import { useRef, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { cn } from '@/lib/utils';
import { BrandMark } from '@/shared/components/BrandMark';
import { useHasMoreBelow } from '../scrollAffordance';
import { SignedInPerson } from './SignedInPerson';

export interface NavRailProps {
  /** Collapses to icons only. Owned by `ShellFrame`, never read here. */
  collapsed: boolean;
  /**
   * What this shell is scoped to — the workspace card, or the organization and the way
   * out of it. The one region where the two shells genuinely differ in kind.
   */
  context?: ReactNode;
  /** The destinations, already gated for this viewer. */
  children: ReactNode;
  className?: string;
}

/**
 * The rail, for whichever shell is rendering it (spec 074 FR-017).
 *
 * Brand at the top, context beneath it, destinations in a scrolling middle, the signed-in
 * person at the foot. Crossing from a workspace into the organization console changes
 * what is *in* those regions and not where they are.
 *
 * **Brand and identity are rendered here rather than passed in.** The contract describes
 * them as slots, and at the call sites they turned out to be byte-identical in both
 * shells — a slot with one possible answer is a choice nobody has, and offering it is how
 * the console came to have no brand block at all. The two slots that do vary are `context`
 * and the navigation, and those are the two this takes.
 */
export function NavRail({
  collapsed,
  context,
  children,
  className,
}: NavRailProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const moreBelow = useHasMoreBelow(scrollRef);

  return (
    <aside
      className={cn(
        // `min-h-0` is what makes the scrolling region below possible: without it this
        // column sizes to its content inside the frame's fixed-height, overflow-hidden
        // shell, and everything past the viewport is simply unreachable — no scrollbar,
        // no way down.
        'bg-sidebar text-sidebar-foreground border-sidebar-border flex min-h-0 shrink-0 flex-col gap-4 border-r py-3',
        collapsed ? 'w-14 px-2' : 'w-60 px-3',
        className,
      )}
    >
      <Link
        to="/"
        className="focus-visible:ring-sidebar-ring flex items-center gap-2.5 rounded-md px-1.5 py-1 focus-visible:ring-3 focus-visible:outline-none"
      >
        {/* `BrandMark`, not an `<img>`: the spade paints with `currentColor` so one
            file serves the Blue and White colourways, and `currentColor` inside an
            externally-referenced SVG resolves against that file rather than the page
            — so the `<img>` this replaces could not follow the theme at all. */}
        <BrandMark variant="spade" className="text-sidebar-foreground size-6" />
        {collapsed ? (
          <span className="sr-only">AI-Powered SDLC — all workspaces</span>
        ) : (
          <span className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">AI-Powered SDLC</span>
            <span className="text-muted-foreground text-xs">
              workspace platform
            </span>
          </span>
        )}
      </Link>

      {context}

      {/* Everything that grows scrolls together as one column, so a workspace with many
          workflows can never take a destination out of reach, and there is never a
          second scrollbar inside the first. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          data-testid="sidebar-scroll"
          className="scrollbar-none flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto"
        >
          {children}
        </div>

        {/* What the hidden scrollbar would have said. Painted over the last few pixels
            of the column and taken away at the end, so it is a statement about this
            list rather than a permanent decoration. */}
        <div
          aria-hidden="true"
          data-testid="sidebar-scroll-fade"
          data-more={moreBelow}
          className="from-sidebar pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t to-transparent opacity-0 transition-opacity data-[more=true]:opacity-100"
        />
      </div>

      {/* Outside the scrolling region: who you are signed in as is not something to
          have to scroll for. */}
      <SignedInPerson collapsed={collapsed} className="flex-none" />
    </aside>
  );
}
